<script setup lang="ts">
import CreateButton from "@/components/shared/CreateButton.vue";
import DeleteButton from "@/components/shared/DeleteButton.vue";
import PrimaryStarButton from "@/components/shared/PrimaryStarButton.vue";
import RowActions from "@/components/shared/RowActions.vue";
import GeneratedFieldControl from "@/components/generated/GeneratedFieldControl.vue";
import { CreatePhoneMemberMeta } from "../../../cbi-generator/generated/dto/CreatePhoneMemberMeta";
import { CreateAddressMemberMeta } from "../../../cbi-generator/generated/dto/CreateAddressMemberMeta";
import { CountryCode } from "../../../cbi-generator/generated/dto/CountryCode";
import { USStateCode } from "../../../cbi-generator/generated/dto/USStateCode";
import { readField, writeField } from "@/composables/generated/fields";
import type { MemberProfilePhoneChange } from "../../../cbi-generator/generated/dto/MemberProfilePhoneChange";
import type { MemberProfileAddressChange } from "../../../cbi-generator/generated/dto/MemberProfileAddressChange";

const phones = defineModel<MemberProfilePhoneChange[]>("phones", {
  required: true,
});
const addresses = defineModel<MemberProfileAddressChange[]>("addresses", {
  required: true,
});
withDefaults(
  defineProps<{
    disabled?: boolean;
    errors?: Record<string, string>;
    show?: ("phones" | "addresses")[];
  }>(),
  { show: () => ["phones", "addresses"] },
);

function addPhone() {
  phones.value.push({
    phone: { phoneNumber: "", phoneType: null },
    association: null,
    name: "Primary",
    primary: !phones.value.some((p) => !p.remove),
    remove: false,
  });
}
function addAddress() {
  addresses.value.push({
    address: {
      line1: "",
      line2: null,
      line3: null,
      city: "",
      state: USStateCode.MA,
      country: CountryCode.US,
      zip: "",
    },
    association: null,
    name: "Home",
    primary: !addresses.value.some((a) => !a.remove),
    remove: false,
  });
}
function selectPrimary(kind: "phones" | "addresses", index: number) {
  const rows = kind === "phones" ? phones.value : addresses.value;
  rows.forEach((row, i) => writeField(row, "primary", i === index));
}
function remove(kind: "phones" | "addresses", index: number) {
  const rows = kind === "phones" ? phones.value : addresses.value;
  if (rows[index].association) {
    writeField(rows[index], "remove", true);
    writeField(rows[index], "primary", false);
  } else rows.splice(index, 1);
}
const phoneLabels: Record<string, string> = {
  phoneNumber: "Phone number",
  phoneType: "Phone type",
};
const addressLabels: Record<string, string> = {
  line1: "Address line 1",
  line2: "Address line 2",
  line3: "Address line 3",
  city: "City",
  state: "State",
  country: "Country",
  zip: "ZIP / postal code",
};
</script>

<template>
  <div class="mt-4">
    <template v-if="show.includes('phones')">
      <div class="d-flex align-center justify-space-between">
        <h3>Phone numbers</h3>
        <CreateButton
          label="Add phone"
          :disabled="disabled"
          @click="addPhone"
        />
      </div>
      <template
        v-for="(row, index) in phones"
        :key="row.association?.assignId ?? `new-${index}`"
      >
        <v-card v-if="!row.remove" variant="outlined" class="pa-4 mb-3">
          <div class="d-flex align-start ga-2">
            <v-text-field
              :model-value="row.name"
              label="Phone label"
              :disabled="disabled"
              @update:model-value="writeField(row, 'name', $event)"
            />
            <RowActions>
              <PrimaryStarButton
                :primary="row.primary"
                :label="`Make phone ${index + 1} primary`"
                primary-label="Primary phone"
                :disabled="disabled"
                @click="selectPrimary('phones', index)"
              />
              <DeleteButton
                :label="`Remove phone ${index + 1}`"
                :disabled="disabled"
                @click="remove('phones', index)"
              />
            </RowActions>
          </div>
          <div v-if="row.phone" class="contact-fields">
            <GeneratedFieldControl
              v-for="field in CreatePhoneMemberMeta.properties"
              :key="field.propertyName"
              :meta="field"
              :label="phoneLabels[field.propertyName] ?? field.propertyName"
              :model-value="readField(row.phone, field.propertyName)"
              :disabled="disabled"
              :error="
                errors?.[`phones.${index}.${field.propertyName}`] ??
                errors?.[`phones.${index}.phone.${field.propertyName}`]
              "
              @update:model-value="
                writeField(row.phone!, field.propertyName, $event)
              "
            />
          </div>
        </v-card>
      </template>
    </template>
    <template v-if="show.includes('addresses')">
      <div
        class="d-flex align-center justify-space-between"
        :class="{ 'mt-6': show.includes('phones') }"
      >
        <h3>Addresses</h3>
        <CreateButton
          label="Add address"
          :disabled="disabled"
          @click="addAddress"
        />
      </div>
      <template
        v-for="(row, index) in addresses"
        :key="row.association?.assignId ?? `new-${index}`"
      >
        <v-card v-if="!row.remove" variant="outlined" class="pa-4 mb-3">
          <div class="d-flex align-start ga-2">
            <v-text-field
              :model-value="row.name"
              label="Address label"
              :disabled="disabled"
              @update:model-value="writeField(row, 'name', $event)"
            />
            <RowActions>
              <PrimaryStarButton
                :primary="row.primary"
                :label="`Make address ${index + 1} primary`"
                primary-label="Primary address"
                :disabled="disabled"
                @click="selectPrimary('addresses', index)"
              />
              <DeleteButton
                :label="`Remove address ${index + 1}`"
                :disabled="disabled"
                @click="remove('addresses', index)"
              />
            </RowActions>
          </div>
          <div v-if="row.address" class="contact-fields">
            <GeneratedFieldControl
              v-for="field in CreateAddressMemberMeta.properties"
              :key="field.propertyName"
              :meta="field"
              :label="addressLabels[field.propertyName] ?? field.propertyName"
              :model-value="readField(row.address, field.propertyName)"
              :disabled="disabled"
              :error="
                errors?.[`addresses.${index}.${field.propertyName}`] ??
                errors?.[`addresses.${index}.address.${field.propertyName}`]
              "
              @update:model-value="
                writeField(row.address!, field.propertyName, $event)
              "
            />
          </div>
        </v-card>
      </template>
    </template>
  </div>
</template>

<style scoped>
.contact-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0 16px;
}
</style>
